import { User, useUserRole } from '@ospace/auth'
import { userTypes } from '@ospace/core-auth'
import { Client } from '@ospace/schemas'
import { ColDef, ConfirmationModal, NewTable, Toggle, UserStatus } from '@ospace/shared'
import { RegionTag } from '@ospace/shared/components/RegionTag'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { useChangeUserStatus } from '../hooks/useChangeUserStatus'
import { useUser } from '../hooks/useUser'
import { DeleteButton } from './DeleteButton'
import { ForgetPasswordButton } from './ForgetPasswordButton'
import { ReinviteButton } from './ReinviteButton'
import { SetPasswordButton } from './SetPasswordButton'
import { Grid, Paper, Select } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'

const ChangeStatusButton = ({ user }: { user: User }) => {
  const { data: currentUser } = useUser()
  const mutation = useChangeUserStatus()
  const label = user.enabled ? 'Disable' : 'Enable'
  const isCurrentUser = user.id === currentUser?.id
  if (isCurrentUser) {
    return null
  }
  return (
    <ConfirmationModal
      alertMessage={
        <div>
          <p className='fs-4'>
            Are you sure you want to <b>{label.toLocaleLowerCase()}</b> the access to the platform
            for{' '}
            <b>
              {user.firstName} {user.lastName}
            </b>
            ?
          </p>
        </div>
      }
      isLoading={mutation.isLoading}
      action={() =>
        mutation.mutateAsync({ username: user.identityId || '', status: !user.enabled })
      }
      renderButton={(props) => <Toggle {...props} checked={user.enabled} onChange={() => {}} />}
    />
  )
}

const UsersTable: React.FC<any> = ({ users }: { users: User[] }) => {
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const usersForTable = [...users]
    .sort((a: any, b: any) => {
      // sort by firstName
      if (a.firstName < b.firstName) return -1
      if (a.firstName > b.firstName) return 1
      // names must be equal
      return 0
    })
    .sort((a: any) => {
      if (a.deletedAt) return 1
      return 0
    })
    .map((user: User) => {
      const clients = new Map()
      user?.client && clients.set(user.client.id, user?.client)
      user?.clients.map((client: Client) => clients.set(client.id, client))
      return {
        ...user,
        clients: Array.from(clients.values()),
        //Insert a space before capital letters
        type: user.type?.replace(/[A-Z]/g, ' $&')?.trim(),
      } as User
    })

  const filteredUsers = usersForTable.filter((user) => {
    if (statusFilter === 'all') return true
    //  this should be based on enabled and deletedAt fields
    if (statusFilter === 'active') return user.enabled && !user.deletedAt
    if (statusFilter === 'inactive') return !user.enabled || user.deletedAt
    return false
  })

  const role = useUserRole()

  const styleDeletedUser = (info: { row: { original: { deletedAt: string | null } } }) => ({
    textDecoration: info.row.original.deletedAt ? 'line-through' : 'none',
    color: info.row.original.deletedAt ? 'gray' : 'none',
    fontSize: '14px',
  })

  //keep elecment same as above, we are just rebuildign the columns to use with new table
  const newTableColumns: ColDef[] = [
    {
      key: 'name',
      label: 'Name',
      element: (user: User) => (
        <span style={styleDeletedUser({ row: { original: user } })}>
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      key: 'username',
      label: 'Username',
      element: (user: User) => (
        <span style={styleDeletedUser({ row: { original: user } })}>{user.email}</span>
      ),
      width: '20%',
    },
    {
      key: 'clients',
      label: 'Clients',
      element: (user: User) => (
        <div className='d-flex' style={styleDeletedUser({ row: { original: user } })}>
          {user.clients.map((client: Client, index: number) => (
            <Link
              key={client.id}
              to={`/client/view/${client?.id}`}
              className={
                user.deletedAt
                  ? 'text-muted text-decoration-none fw-bolder d-block mb-1 fs-6 me-1'
                  : 'text-black text-hover-primary fw-bolder d-block mb-1 fs-6 me-1'
              }
            >
              {client?.name}
              {index === user.clients.length - 1 ? `` : `,`}
            </Link>
          ))}
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      element: (user: User) => (
        <span style={styleDeletedUser({ row: { original: user } })}>
          {user.type?.replace(/[A-Z]/g, ' $&')?.trim()}
        </span>
      ),
    },
    {
      key: 'userStatus',
      label: 'Status',
      element: (user: User) => (
        <span
          style={{ ...styleDeletedUser({ row: { original: user } }), fontSize: '12px' }}
          className={
            user.userStatus === UserStatus.FORCE_CHANGE_PASSWORD
              ? 'badge badge-light-warning'
              : 'badge badge-light-success'
          }
        >
          {user.userStatus}
        </span>
      ),
    },
    {
      key: 'enabled',
      label: 'Enabled',
      element: (user: User) => <ChangeStatusButton user={user} />,
    },
    {
      key: 'region',
      label: 'Region',
      element: (user: User) => (
        <div style={styleDeletedUser({ row: { original: user } })}>
          <RegionTag regionCode={user.region as string} />
        </div>
      ),
    },
  ]

  if (role === userTypes.Admin || role === userTypes.ClientManager) {
    newTableColumns.push(
      {
        key: 'forget-password',
        label: 'Forget Password',
        element: (user: User) => <ForgetPasswordButton user={user} />,
      },
      {
        key: 'set-password',
        label: 'Set Password',
        element: (user: User) => <SetPasswordButton user={user} />,
      },
      {
        key: 'delete',
        label: 'Delete',
        element: (user: User) => <DeleteButton user={user} />,
      },
      {
        key: 'actions',
        label: 'More actions',
        element: (user: User) => (
          <div>
            <Link
              to={`/user/edit/${user.identityId}`}
              className='btn btn-secondary btn-sm px-4 me-2'
            >
              Edit
            </Link>
            {user.userStatus === UserStatus.FORCE_CHANGE_PASSWORD && <ReinviteButton user={user} />}
          </div>
        ),
      }
    )
  }
  return (
    <Paper my={'lg'}>
      <Grid justify='space-between' align='center' p='xl'>
        <Grid.Col span={6}>
          <h1 className='m-0'>
            All Users <span style={{ fontSize: '12px' }}>({filteredUsers.length})</span>
          </h1>
        </Grid.Col>

        <Grid.Col span={6}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Select
              placeholder='Filter by status'
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'all')}
              data={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              w={180}
            />

            <Link to='/user/create' className='btn btn-primary btn-sm'>
              <IconPlus size={'14'} /> New User
            </Link>
          </div>
        </Grid.Col>
      </Grid>

      <NewTable colDef={newTableColumns} tableData={filteredUsers} fixFirstColumn={true} />
    </Paper>
  )
}

export { UsersTable }
